import {
	Component,
	OnInit,
	OnDestroy,
	inject,
	TemplateRef,
	WritableSignal,
	signal,
	CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	ActivatedRoute,
	Router,
	RouterOutlet,
	RouterLink,
	NavigationEnd,
	ChildrenOutletContexts,
} from '@angular/router';
import { routeTransitionAnimations } from '../animations/route-animations';
import {
	NgbModule,
	NgbOffcanvas,
	OffcanvasDismissReasons,
	NgbModal,
} from '@ng-bootstrap/ng-bootstrap';
import { takeWhile, map, Observable, of } from 'rxjs';
import OpenAI from 'openai';

import { NotificationService } from '../notification-service/notification.service';
import { NotificationHandle } from '../notification/notification-handle';
import { LoginService } from '../login-service/login.service';
import { DynamicModalWrapperService } from '@meta-press/shared-plugins';
import { ColorPaletteManagementComponent } from '../components/color-palette-management/color-palette-management.component';
import { PresetManagementComponent } from '../components/preset-management/preset-management.component';

import { AIModel } from '../model/AIModel';
import { AiService, AIServiceType } from '../services/ai/ai.service';
import { Roles } from '@meta-press/shared-plugins';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { OpenAIService } from '../services/openai-service';
import { TagManagementModalComponent } from '../tag-management-modal/tag-management-modal.component';
import { UserProfileModalComponent } from '../user-profile-modal/user-profile-modal.component';
import { UpdateNotificationModalComponent } from '../update-notification-modal/update-notification-modal.component';
import { PrivacyPolicyModalComponent } from '../privacy-policy-modal/privacy-policy-modal.component';
import { BackendService } from '../backend-service/backend.service';
import { SettingsService } from '../services/settings-service/settings.service';
import { BatchGenerationService } from '../services/batch-generation.service';
import { GenerationLanguageDropdownComponent } from '../components/generation-language-dropdown/generation-language-dropdown.component';

@Component({
	selector: 'app-content',
	standalone: true,
	imports: [
		RouterOutlet,
		NgbModule,
		RouterLink,
		CommonModule,
		GenerationLanguageDropdownComponent,
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	templateUrl: './app-content.component.html',
	styleUrl: './app-content.component.scss',
	animations: [routeTransitionAnimations],
})
export class AppContentComponent implements OnInit, OnDestroy {
	private destroy = true; // Flag to control observable subscriptions
	currentURL = '';

	readonly AIModel: typeof AIModel = AIModel;
	readonly AiService: typeof AIServiceType = AIServiceType;

	protected shouldShowBackgroundLoadingSpinner = false;
	protected readonly Roles: typeof Roles = Roles;
	protected tokenUsage: number | null = null;
	protected isLoadingTokenUsage = false;

	// Notification handle for vector store operations
	private vectorStoreProgressHandle: NotificationHandle | null = null;

	// Hover timers for notification dropdown
	private dropdownHoverTimer: any = null;
	private dropdownLeaveTimer: any = null;
	private readonly HOVER_DELAY = 300; // ms delay before opening
	private readonly LEAVE_DELAY = 500; // ms delay before closing

	/**
	 * Check if there are unsaved batch jobs for notification badge
	 */
	get hasUnsavedBatchJobs(): boolean {
		return this.batchGenerationService.getCompletedBatchJobs().length > 0;
	}

	/**
	 * Get count of unsaved batch jobs (completed jobs ready for saving)
	 */
	get unsavedBatchJobsCount(): number {
		return this.batchGenerationService.getCompletedBatchJobs().length;
	}

	/**
	 * Check if there are active batch jobs (queued or processing)
	 */
	get hasActiveBatchJobs(): boolean {
		return this.getActiveBatchJobsCount() > 0;
	}

	/**
	 * Get count of active batch jobs (queued + processing)
	 */
	getActiveBatchJobsCount(): number {
		return (
			this.batchGenerationService.getJobCount('queued') +
			this.batchGenerationService.getJobCount('processing')
		);
	}

	/**
	 * Get total batch jobs count (all statuses)
	 */
	getTotalBatchJobsCount(): number {
		return this.batchGenerationService.getBatchJobs().length;
	}

	/**
	 * Get batch jobs progress display text (ready/total)
	 */
	get batchJobsProgressText(): string {
		const completed = this.batchGenerationService.getJobCount('completed');
		const total = this.getTotalBatchJobsCount();

		if (total === 0) return '';
		return `${completed}/${total}`;
	}

	/**
	 * Get animation data from the current route for router animations
	 * This is used to trigger slide+fade transitions between routes
	 */
	getRouteAnimationData() {
		return this.contexts.getContext('primary')?.route?.snapshot?.data?.[
			'animation'
		];
	}

	constructor(
		public route: ActivatedRoute,
		router: Router,
		private contexts: ChildrenOutletContexts,

		protected sourceMaterialService: SourceMaterialService,
		protected aiService: AiService,
		protected notificationService: NotificationService,
		protected loginService: LoginService,
		private openAIService: OpenAIService,
		private backendService: BackendService,
		private offcanvasService: NgbOffcanvas,
		private modalService: NgbModal,
		private dynamicModalService: DynamicModalWrapperService,
		protected settingsService: SettingsService,
		protected batchGenerationService: BatchGenerationService,
	) {
		// React to route changes and keep track of current route path
		router.events.subscribe((value) => {
			if (value instanceof NavigationEnd) {
				const navigationEnd = value as NavigationEnd;
				console.log(
					'Route changed to: ' + navigationEnd.urlAfterRedirects,
				);
				this.currentURL = navigationEnd.urlAfterRedirects;
			}
		});

		this.loginService.OnLogin.pipe(takeWhile(() => this.destroy)).subscribe(
			() => {
				const user = this.loginService.GetCurrentUser();
				if (user) {
					this.sourceMaterialService.ClearSelection();
					// this.sourceMaterialService.SetArticles(user.articles);

					// Check vector store status
					// Check if user has a vector storage setup
					// this.aiService.ListVectorStores(user).subscribe({
					// 	next: (stores: string[]) => {
					// 		if (stores.length == 1) {
					// 			// this.notificationService.ShowToast("Vektoritietokanta löytyy. Voit käyttää sitä lähdemateriaalina.", 'success');
					// 		} else if (stores.length > 1) {
					// 			this.notificationService.ShowToast(
					// 				'Vektoritietokantoja löytyy useita. Tämä aiheuttaa ongelmia. Poista kannat ja lataa sivu uudestaan.',
					// 				'danger',
					// 			);
					// 		} else {
					// 			// this.notificationService.ShowToast("Vektoritietokantaa ei löytynyt. Luodaan...", 'warning');

					// 			this.CreateVectorStore().subscribe({
					// 				next: (
					// 					store:
					// 						| OpenAI.VectorStores.VectorStore
					// 						| undefined,
					// 				) => {
					// 					if (store) {
					// 						this.notificationService.ShowToast(
					// 							'Vektoritietokanta luotu.',
					// 							'success',
					// 						);
					// 					} else {
					// 						this.notificationService.ShowToast(
					// 							'Vektoritietokannan luonti epäonnistui.',
					// 							'danger',
					// 						);
					// 					}
					// 				},
					// 				error: (error) => {
					// 					this.notificationService.ShowToast(
					// 						'Vektoritietokannan luonti epäonnistui. Virhe: ' +
					// 							error,
					// 						'danger',
					// 					);
					// 				},
					// 			});
					// 		}
					// 	},
					// 	error: (error) => {
					// 		this.notificationService.ShowToast(
					// 			'Vektoritietokannan tarkistaminen epäonnistui. Virhe: ' +
					// 				error,
					// 			'danger',
					// 		);
					// 	},
					// });
				} else {
					console.log('Login failed - no user info found');
				}
			},
		);

		this.notificationService.backgroundSpinnerState
			.pipe(takeWhile(() => this.destroy))
			.subscribe((show: boolean) => {
				this.shouldShowBackgroundLoadingSpinner = show;
			});

		// Listen to batch generation events to trigger UI updates
		this.batchGenerationService.onJobAdded
			.pipe(takeWhile(() => this.destroy))
			.subscribe(() => {
				// Job added - UI will update automatically via getters
			});

		this.batchGenerationService.onJobCompleted
			.pipe(takeWhile(() => this.destroy))
			.subscribe(() => {
				// Job completed - UI will update automatically via getters
			});

		this.batchGenerationService.onJobFailed
			.pipe(takeWhile(() => this.destroy))
			.subscribe(() => {
				// Job failed - UI will update automatically via getters
			});
	}

	protected GetAIModelEnumNumber(aiModel: string): number {
		// Go through all the AIModel enum values and return the one that matches the given string
		for (const model in AIModel) {
			if (model == aiModel) {
				return Object.keys(AIModel).indexOf(model);
			}
		}

		throw new Error('Invalid AI model string: ' + aiModel);
		return 0;
	}

	ngOnInit() {
		console.log('[AppContentComponent] ngOnInit called');
		this.aiService.SetModelById('gpt-4.1');

		// Check if app was just updated (either automatically or manually)
		const appWasUpdated = sessionStorage.getItem('appWasUpdated');
		// Check if this is a fresh install
		const appInstallType = sessionStorage.getItem('appInstallType');

		console.log(
			'[UPDATE] Flag check in app-content ngOnInit:',
			appWasUpdated,
		);
		console.log('[VERSION] Install type check:', appInstallType);
		console.log('[UPDATE] Flag === "auto"?', appWasUpdated === 'auto');
		console.log('[UPDATE] Flag === "manual"?', appWasUpdated === 'manual');

		if (appWasUpdated === 'auto') {
			console.log('[UPDATE] Showing auto-update toast...');
			this.notificationService.ShowToast(
				'Sovellus päivitettiin uuteen versioon automaattisesti.',
				'success',
			);
		}

		// Show update notification modal if app was updated by any method OR if it's a fresh install
		if (
			appWasUpdated === 'auto' ||
			appWasUpdated === 'manual' ||
			appInstallType === 'fresh-install'
		) {
			console.log(
				'[UPDATE] Conditions met, opening update notification modal...',
			);
			// Clear the flags immediately to prevent showing modal again
			sessionStorage.removeItem('appWasUpdated');
			sessionStorage.removeItem('appInstallType');

			// Show the update notification modal
			console.log('[UPDATE] Calling openUpdateNotificationModal()...');
			this.openUpdateNotificationModal();
			console.log('[UPDATE] openUpdateNotificationModal() called');
		} else {
			console.log(
				'[UPDATE] No update or fresh install flag found, skipping modal',
			);
		}
	}

	ngOnDestroy(): void {
		this.destroy = false; // Mark the component as destroyed

		// Clean up hover timers
		if (this.dropdownHoverTimer) {
			clearTimeout(this.dropdownHoverTimer);
		}
		if (this.dropdownLeaveTimer) {
			clearTimeout(this.dropdownLeaveTimer);
		}
	}

	closeResult: WritableSignal<string> = signal('');

	/**
	 * Toggle notification container visibility
	 */
	protected toggleNotifications(): void {
		this.notificationService.toggleContainerVisibility();
	}

	/**
	 * Handle mouse enter on bell icon
	 */
	protected onNotificationBellHover(dropdown: any): void {
		// Clear any pending close timer
		if (this.dropdownLeaveTimer) {
			clearTimeout(this.dropdownLeaveTimer);
			this.dropdownLeaveTimer = null;
		}

		// Open dropdown after short delay
		this.dropdownHoverTimer = setTimeout(() => {
			dropdown.open();
		}, this.HOVER_DELAY);
	}

	/**
	 * Handle mouse leave from bell icon
	 */
	protected onNotificationBellLeave(dropdown: any): void {
		// Clear any pending open timer
		if (this.dropdownHoverTimer) {
			clearTimeout(this.dropdownHoverTimer);
			this.dropdownHoverTimer = null;
		}

		// Start close timer
		this.dropdownLeaveTimer = setTimeout(() => {
			dropdown.close();
		}, this.LEAVE_DELAY);
	}

	/**
	 * Handle mouse enter on dropdown menu
	 */
	protected onNotificationDropdownHover(dropdown: any): void {
		// Clear any pending close timer
		if (this.dropdownLeaveTimer) {
			clearTimeout(this.dropdownLeaveTimer);
			this.dropdownLeaveTimer = null;
		}
		// Keep dropdown open
		dropdown.open();
	}

	/**
	 * Handle mouse leave from dropdown menu
	 */
	protected onNotificationDropdownLeave(dropdown: any): void {
		// Close dropdown after delay
		this.dropdownLeaveTimer = setTimeout(() => {
			dropdown.close();
		}, this.LEAVE_DELAY);
	}

	/**
	 * Format timestamp for display (relative time)
	 */
	protected formatTimestamp(timestamp: number): string {
		const now = Date.now();
		const diff = now - timestamp;
		const seconds = Math.floor(diff / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);

		if (days > 0) {
			return `${days} päivää sitten`;
		} else if (hours > 0) {
			return `${hours} tuntia sitten`;
		} else if (minutes > 0) {
			return `${minutes} minuuttia sitten`;
		} else {
			return 'Juuri äsken';
		}
	}

	/**
	 * Track notifications by ID for ngFor optimization
	 */
	protected trackByNotificationId(index: number, notification: any): string {
		return notification.id;
	}

	open(content: TemplateRef<unknown>) {
		// Load token usage when opening the menu
		this.loadTokenUsage();

		this.offcanvasService
			.open(content, {
				ariaLabelledBy: 'offcanvas-basic-title',
				position: 'end',
				scroll: true,
			})
			.result.then(
				(result) => {
					this.closeResult.set(`Closed with: ${result}`);
				},
				(reason) => {
					this.closeResult.set(
						`Dismissed ${this.getDismissReason(reason)}`,
					);
				},
			);
	}

	private getDismissReason(reason: OffcanvasDismissReasons | string): string {
		switch (reason) {
			case OffcanvasDismissReasons.ESC:
				return 'by pressing ESC';
			case OffcanvasDismissReasons.BACKDROP_CLICK:
				return 'by clicking on the backdrop';
			default:
				return `with: ${reason}`;
		}
	}

	protected CloseOffcanvas(): void {
		this.offcanvasService.dismiss();
	}

	protected openAdvancedFeatures(content: TemplateRef<any>): void {
		this.modalService.open(content, { centered: true });
	}

	protected onManageTags(): void {
		// Open tag management modal
		this.modalService.open(TagManagementModalComponent, {
			size: 'lg',
			backdrop: 'static',
			keyboard: false,
		});
	}

	protected openUserProfile(): void {
		// Open user profile modal
		this.modalService.open(UserProfileModalComponent, {
			size: 'lg',
			backdrop: 'static',
			keyboard: false,
		});
	}

	/**
	 * Open update notification modal showing version changes and known issues
	 */
	protected openUpdateNotificationModal(): void {
		this.modalService.open(UpdateNotificationModalComponent, {
			centered: true,
			size: 'lg',
			backdrop: 'static',
		});
	}

	/**
	 * Open privacy policy modal (tietosuojaseloste)
	 */
	protected openPrivacyPolicyModal(): void {
		this.modalService.open(PrivacyPolicyModalComponent, {
			centered: true,
			size: 'lg',
			backdrop: 'static',
		});
	}

	protected onManageColorPalettes(): void {
		// Open color palette management modal
		this.dynamicModalService.openComponentModal(
			ColorPaletteManagementComponent,
			{
				title: 'Värien hallinta',
				size: 'xl',
				showFooter: false,
			},
		);
	}

	/**
	 * Open style presets management modal for managing saved style presets
	 */
	protected onManageStylePresets(): void {
		// Open style preset management modal
		this.dynamicModalService.openComponentModal(PresetManagementComponent, {
			title: 'Tyyliasetusten hallinta',
			size: 'xl',
			showFooter: false,
		});
	}

	private loadTokenUsage(): void {
		this.isLoadingTokenUsage = true;
		this.tokenUsage = null;

		// Get start and end of current month
		const now = new Date();
		const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
		const endOfMonth = new Date(
			now.getFullYear(),
			now.getMonth() + 1,
			0,
			23,
			59,
			59,
			999,
		);

		// Convert to Unix timestamps in seconds
		const unixStartDate = Math.floor(startOfMonth.getTime() / 1000);
		const unixEndDate = Math.floor(endOfMonth.getTime() / 1000);

		this.backendService
			.GetCurrentUserToken(unixStartDate, unixEndDate)
			.subscribe({
				next: (usage) => {
					this.tokenUsage = usage;
					this.isLoadingTokenUsage = false;
				},
				error: (error) => {
					console.error('Error loading token usage:', error);
					this.tokenUsage = null;
					this.isLoadingTokenUsage = false;
					this.notificationService.ShowToast(
						'Token-kulutuksen lataus epäonnistui',
						'danger',
					);
				},
			});
	}

	/**
	 * Preload users data when hovering over the Käyttäjät tab
	 */
	protected onUsersTabHover(): void {
		// Only preload if user has permission
		const currentUser = this.loginService.GetCurrentUser();
		if (
			currentUser?.permission === Roles.SuperAdmin ||
			currentUser?.permission === Roles.Admin
		) {
			// Trigger lazy loading without waiting for the result
			// This ensures data is ready when the user clicks the tab
			this.loginService.ensureUsersLoaded().subscribe({
				next: (loaded) => {
					if (loaded) {
						console.log('Users preloaded on hover');
					}
				},
				error: (error) => {
					console.error('Error preloading users on hover:', error);
				},
			});

			// Also preload organizations if user is Admin (they need it for filtering)
			if (currentUser?.permission === Roles.Admin) {
				this.loginService.ensureOrganizationsLoaded().subscribe({
					next: (loaded) => {
						if (loaded) {
							console.log(
								'Organizations preloaded for admin on hover',
							);
						}
					},
					error: (error) => {
						console.error(
							'Error preloading organizations on hover:',
							error,
						);
					},
				});
			}
		}
	}

	/**
	 * Preload organizations data when hovering over the Organisaatiot tab
	 */
	protected onOrganizationsTabHover(): void {
		// Only preload if user has permission (SuperAdmin only)
		const currentUser = this.loginService.GetCurrentUser();
		if (currentUser?.permission === Roles.SuperAdmin) {
			// Trigger lazy loading without waiting for the result
			// This ensures data is ready when the user clicks the tab
			this.loginService.ensureOrganizationsLoaded().subscribe({
				next: (loaded) => {
					if (loaded) {
						console.log('Organizations preloaded on hover');
					}
				},
				error: (error) => {
					console.error(
						'Error preloading organizations on hover:',
						error,
					);
				},
			});
		}
	}
}
